import { useCallback } from 'react';
import type { ActionMeta, OptionType } from '@atlaskit/select';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useFlagService } from '@atlassian/jira-flags';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { CLIENT_ERROR_403, CLIENT_ERROR_404 } from '../../common/errors-constants.tsx';
import { useConnectProjects } from '../use-connect-project/index.tsx';
import { useConnectedProjectsChangeAnalytics } from '../use-connected-projects-change-analytics/index.tsx';
import { useDisconnectProjects } from '../use-disconnect-project/index.tsx';
import type {
	NewlyConnectedProject,
	OnProjectConnectOptions,
	OnProjectDisconnectOptions,
	PreviouslyConnectedProject,
} from './types.tsx';
import {
	createClearProjectsMultiSuccessFlagConfig,
	createProjectsConnectionMultiSuccessFlagConfig,
	createProjectsDisconnectionMultiSuccessFlagConfig,
} from './utils.tsx';

const mapActionMeta = ({ action }: ActionMeta<OptionType>) => {
	switch (action) {
		case 'deselect-option':
			return 'unselected';
		default:
			return 'cleared';
	}
};

export const useConnectedProjects = (currentProjectId: string) => {
	const cloudId = useCloudId();
	const currentProjectAri = JiraProjectAri.create({
		siteId: cloudId,
		projectId: currentProjectId,
	}).toString();
	const { showFlag } = useFlagService();
	const [connectProjects, connectingProjects] = useConnectProjects(currentProjectAri);
	const [disconnectProjects, disconnectingProjects] = useDisconnectProjects(currentProjectAri);
	const fireProjectChangeAnalytics = useConnectedProjectsChangeAnalytics(
		'multiConnectedProjectPicker',
	);

	const onProjectDisconnect = useCallback(
		async (project: PreviouslyConnectedProject, options?: OnProjectDisconnectOptions) => {
			const projectAri = JiraProjectAri.create({
				siteId: cloudId,
				projectId: project.id,
			}).toString();

			try {
				await disconnectProjects([projectAri]);
			} catch (error) {
				// Handling two corner cases as per previous REST-API-based implementation
				// https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/72011/diff#jira%2Fsrc%2Fpackages%2Fbusiness%2Fcalendar%2Fsrc%2Fcontrollers%2Fconnected-projects%2Findex.tsx?t=195
				if (
					error instanceof Error &&
					(error.message === CLIENT_ERROR_403 || error.message === CLIENT_ERROR_404)
				) {
					options?.onClientError?.(error);
				}
				return;
			}
			showFlag(createProjectsDisconnectionMultiSuccessFlagConfig());
			fireProjectChangeAnalytics(
				options?.actionMeta ? mapActionMeta(options.actionMeta) : 'cleared',
			);
		},
		[cloudId, disconnectProjects, fireProjectChangeAnalytics, showFlag],
	);

	const onDisconnectAllProjects = useCallback(
		async (projects: PreviouslyConnectedProject[]) => {
			const projectAris = projects.map((project) =>
				JiraProjectAri.create({
					siteId: cloudId,
					projectId: project.id,
				}).toString(),
			);

			try {
				await disconnectProjects(projectAris);
			} catch (error) {
				// We don't need to handle 403 and 404 because it's a bulk operation
				// all other errors are being handled and shown to the user by "disconnectProjects" already
				return;
			}
			showFlag(createClearProjectsMultiSuccessFlagConfig());
			fireProjectChangeAnalytics('cleared');
		},
		[cloudId, disconnectProjects, fireProjectChangeAnalytics, showFlag],
	);

	const onProjectConnect = useCallback(
		async (
			project: NewlyConnectedProject,
			options: OnProjectConnectOptions = {
				showSuccessFlag: true,
				fireAnalyticsOnSuccess: true,
			},
		) => {
			const transformedNewlyConnectedProject = {
				id: project.id,
				name: project.name,
				avatarUrl: project.avatarUrl,
				resourceId: JiraProjectAri.create({
					siteId: cloudId,
					projectId: project.id,
				}).toString(),
			};

			try {
				await connectProjects(transformedNewlyConnectedProject);
			} catch (error) {
				// error is being handled and shown to the user by "connectProjects" already
				return;
			}

			if (options.showSuccessFlag) {
				showFlag(createProjectsConnectionMultiSuccessFlagConfig());
			}

			if (options.fireAnalyticsOnSuccess) {
				fireProjectChangeAnalytics('selected', {
					// The multi project picker doesn't have a scenario of "replacing" a connected project
					isSwitchingConnectedProject: false,
				});
			}
		},
		[cloudId, connectProjects, fireProjectChangeAnalytics, showFlag],
	);

	return {
		initialised: !connectingProjects && !disconnectingProjects,
		loading: connectingProjects || disconnectingProjects,
		onProjectDisconnect,
		onDisconnectAllProjects,
		onProjectConnect,
	};
};
